// React
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'

// Libraries
import { withTranslation } from 'shared/utils/withTranslation'

// Components
import { ModalCartPicker } from 'store/modules/cart/CartPicker/Modal'
import BPeriod from '../../blocks/Period'

// Shared
import Format from 'shared/utils/format'
import quickviewActions from 'store/v2/redux/actions/quickview'
import { triggerUserFrameworkBookEvent } from 'shared/utils/userFrameworkEvents'
import ClientActions from 'store/v2/redux/actions/client'
import ItemActions from 'store/v2/redux/actions/item'
import CartActions from 'store/v2/redux/actions/cart'
import { isLocationInputEnabled } from 'ui/components/Picker/utils'
import useIsMobile from 'shared/utils/useIsMobile'

// Styles
import { Container, DateTimeContainer } from './Base.styles'

export class SeparateDatePicker extends Component {
  constructor (props) {
    super(props)

    this.state = {
      modalOpen: false,
      loading: false
    }
  }

  static displayName = 'SeparateDatePicker'

  static propTypes = {
    periodType: PropTypes.string,
    useAMOrPM: PropTypes.bool,
    locations: PropTypes.object,
    cart: PropTypes.object,
    timeFormat: PropTypes.string,
    useTimes: PropTypes.bool,
    brandingColor: PropTypes.string,
    productId: PropTypes.string,
    item: PropTypes.object,
    isMobile: PropTypes.bool,
    title: PropTypes.string,
    t: PropTypes.func,
    behavior: PropTypes.string
  }

  componentDidMount = () => {
    const { productId } = this.props

    // Fetch item if id set
    if (productId) {
      this.setState({ loading: true })
      ItemActions.fetchAll({
        include: 'photo',
        apiVersion: 3,
        ids: [productId]
      }).then(() => {
        this.setState({ loading: false })
      })
    }
  }

  handleSave = () => {
    const { item, productId } = this.props

    if (!productId) return

    if (!item) {
      // Item not in state as we can't fetch products trough the item endpoint
      // so it's a product and should be booked
      CartActions.book({ item_id: productId, quantity: 1, apiVersion: 1 }).then(() => {
        triggerUserFrameworkBookEvent(productId, 1)
        // Implemented in PR#6092
        ClientActions.setCheckingOut()
      })
    } else {
      quickviewActions.setItem(productId)
    }
  }

  handleClose = () => {
    this.setState({ modalOpen: false })
  }

  handleOpenModal = async () => {
    const { cart, behavior } = this.props
    if (behavior === 'checkout' && cart?.created_at) {
      await CartActions.clear()
    }

    this.setState({ modalOpen: true })
  }

  handleTouch = (e) => {
    e.preventDefault()
    this.handleOpenModal()
  }

  render () {
    const {
      useAMOrPM,
      useTimes,
      periodType,
      brandingColor,
      cart,
      locations,
      productId,
      item,
      title,
      isMobile,
      t
    } = this.props

    const {
      modalOpen
    } = this.state

    const hasLocations = locations.size() > 1

    return (
      <Container
        useAMPM={useAMOrPM}
        brandingColor={brandingColor}
      >
        <DateTimeContainer
          onClick={this.handleOpenModal}
          onTouchEnd={this.handleTouch}
          brandingColor={brandingColor}
          className="from"
        >
          {cart && cart.starts_at ?
            (
              <BPeriod.Detail>
                {hasLocations && isLocationInputEnabled('start') && (
                  <BPeriod.Location>
                    {cart && cart.start_location_id ? locations.getById(cart.start_location_id).name : null}
                  </BPeriod.Location>
                )}
                <BPeriod.DateTime>
                  {`${Format.date(
                    cart ? cart.starts_at : undefined,
                    'MMM D[,] YYYY'
                  )}${useTimes ?
                    `, ${Format.date(
                      cart ? cart.starts_at : undefined,
                      this.props.timeFormat
                    )}` :
                    ''}`}
                </BPeriod.DateTime>
              </BPeriod.Detail>
            ) :
            (
              <BPeriod.Detail>
                <BPeriod.Placeholder>
                  {t('user.store.start_date')}
                </BPeriod.Placeholder>
              </BPeriod.Detail>
            )}
        </DateTimeContainer>
        <DateTimeContainer
          onClick={this.handleOpenModal}
          onTouchEnd={this.handleOpenModal}
          disabled={['timeslot_fixed', 'timeslot_duration'].includes(periodType)}
          brandingColor={brandingColor}
          className="till"
        >
          {cart && cart.stops_at ?
            (
              <BPeriod.Detail>
                {hasLocations && isLocationInputEnabled('stop') && (
                  <BPeriod.Location>
                    {cart && cart.stop_location_id ? locations.getById(cart.stop_location_id).name : null}
                  </BPeriod.Location>
                )}
                <BPeriod.DateTime>
                  {`${Format.date(
                    cart ? cart.stops_at : undefined,
                    'MMM D[,] YYYY'
                  )}${useTimes ?
                    `, ${Format.date(
                      cart ? cart.stops_at : undefined,
                      this.props.timeFormat
                    )}` :
                    ''}`}
                </BPeriod.DateTime>
              </BPeriod.Detail>
            ) :
            (
              <BPeriod.Detail>
                <BPeriod.Placeholder>
                  {t('user.store.stop_date')}
                </BPeriod.Placeholder>
              </BPeriod.Detail>
            )}
        </DateTimeContainer>
        <ModalCartPicker
          cart={cart}
          onSave={this.handleSave}
          open={modalOpen}
          onClose={this.handleClose}
          brandingColor={this.props.brandingColor}
          direction={isMobile ? 'vertical' : 'horizontal'}
          isMobile={isMobile}
          title={item?.name || title}
          productId={productId}
          loading={this.state.loading}
        />
      </Container>
    )
  }
}

const FunctionalSeparateDatePicker = (props) => {
  const isMobile = useIsMobile()

  return <SeparateDatePicker {...props} isMobile={isMobile} />
}

const mapStateToProps = (state, props) => {
  const useAMOrPM = state.settings.get('dates.use_am_pm')
  const useTimes = state.settings.get('store.use_times')
  const behavior = state.settings.get('store.behaviors.add_button')
  const brandingColor = window.booqableOptions?.primaryColor ?? state.settings.get('store.brand_color')
  const cart = state.orm.carts.getById(state.client.cartId)

  let timeFormat = ''

  if (useTimes) {
    timeFormat = useAMOrPM ? ' hh:mm A' : ' HH:mm'
  }

  return {
    cart,
    useTimes,
    item: state.orm.items.getById(props.productId),
    brandingColor,
    locations: state.orm.locations.getAll().sort('name'),
    timeFormat,
    behavior
  }
}

const reduxComponent = reduxConnect(mapStateToProps, null)(FunctionalSeparateDatePicker)

export default withTranslation('user')(reduxComponent)
